import React, { useState } from 'react';
import { PlaySheet } from '../../components/PlaySheet';
import { PlayPrimaryButton } from '../../components/PlayPrimaryButton';
import { useAddWalkInMutation } from 'store/services/play/playApi';
import { playErrorMessage } from 'utils/play/playErrors';
import type { TournamentState } from 'types/interfaces/play';
import './HostLobby.css';

interface Props {
  code: string;
  state: TournamentState;
  onClose: () => void;
}

/**
 * Decision 17: a walk-in placeholder (name only, no account). The helper line
 * depends on whether a round has been paired (Final copy block).
 */
const AddPlayerSheet: React.FC<Props> = ({ code, state, onClose }) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [addWalkIn, { isLoading }] = useAddWalkInMutation();
  const roundsSoFar = state.rounds.length;
  const current = state.currentRound?.number ?? roundsSoFar;

  const submit = async () => {
    const displayName = name.trim();
    if (!displayName || isLoading) return;
    setError(null);
    try {
      await addWalkIn({ id: state.tournament.id, code, displayName }).unwrap();
      onClose();
    } catch (err) {
      setError(playErrorMessage(err, "Couldn't add the player."));
    }
  };

  return (
    <PlaySheet ariaLabel="Add a player" onClose={onClose}>
      <h2 className="play-sheet-title">Add a player</h2>
      <p className="play-helper">{roundsSoFar === 0 ? 'For walk-ins without a phone.' : `Late entry joins from round ${current + 1} with a 0-${roundsSoFar} record.`}</p>
      <div className="play-field" style={{ marginTop: 14 }}>
        <label className="play-field__label" htmlFor="play-walkin-name">
          Player name
        </label>
        <input
          id="play-walkin-name"
          className="play-native-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name as it should appear on pairings"
          maxLength={40}
          autoFocus
        />
      </div>
      <div className="play-sheet-actions">
        <PlayPrimaryButton label="Add player" disabled={!name.trim()} loading={isLoading} onClick={submit} />
      </div>
      {error && (
        <p className="play-error" role="alert">
          {error}
        </p>
      )}
    </PlaySheet>
  );
};

export default AddPlayerSheet;
